import { createFileRoute, useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { CheckWixButton } from "@/components/admin/CheckWixButton";
import { listWixOrders } from "@/lib/wix/wix.functions";
import { WixOrderCard } from "@/components/admin/WixOrderCard";
import { useState } from "react";
import { Search, Package } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/wix")({
  head: () => ({
    meta: [
      { title: "Админ — Bulgaria 3D Print поръчки | FIXHEN" },
      { name: "description", content: "Поръчки от Bulgaria 3D Print: клиенти, продукти, адреси, плащания и история във FIXHEN." },
      { property: "og:title", content: "Bulgaria 3D Print — поръчки | FIXHEN" },
      { property: "og:description", content: "Подробен преглед на Wix поръчки, доставка, продукти и плащания." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminLayout>
      <WixOrdersEntry />
    </AdminLayout>
  ),
});

function WixOrdersEntry() {
  const hash = useLocation({ select: (location) => location.hash });
  return <WixOrdersPage key={hash} selectedHash={hash} />;
}

function WixOrdersPage({ selectedHash }: { selectedHash: string }) {
  const list = useServerFn(listWixOrders);
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["wix-orders"], queryFn: () => list() });
  const [search, setSearch] = useState("");
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(() => new Set(selectedHash ? [selectedHash] : []));
  const orders = (data ?? []).filter((o: any) => [o.order_number, o.customer_name, o.email, o.phone, o.city, o.country, ...(o.items ?? []).flatMap((i: any) => [i.name, i.sku])].some((v) => String(v ?? "").toLowerCase().includes(search.trim().toLowerCase())));
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-8 lg:p-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Bulgaria 3D Print</h1>
          <p className="mt-2 text-sm text-muted-foreground">Поръчки · от 12.10.2026</p>
        </div>
        <CheckWixButton />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 border-y py-4"><div className="flex items-center gap-2 text-sm font-medium"><Package className="size-4 text-primary" />{data?.length ?? 0} поръчки</div><label className="relative w-full sm:w-80"><Search className="absolute left-3 top-3 size-4 text-muted-foreground" /><input aria-label="Търси Wix поръчки" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="№, клиент, продукт, SKU…" className="h-10 w-full rounded-md border bg-card pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring" /></label></div>
      {error ? <div role="alert" className="space-y-3 text-sm text-destructive"><p>{error.message}</p><Button variant="outline" onClick={() => refetch()}>Опитай отново</Button></div> : isLoading ? <p className="text-sm text-muted-foreground">Зареждане…</p> : !orders.length ? (
        <p className="py-12 text-center text-sm text-muted-foreground">{search ? "Няма съвпадащи поръчки." : "Още няма поръчки."}</p>
      ) : (
        <div className="grid gap-3">
          {orders.map((o: any) => {
            const anchor = `wix-${o.wix_order_id ?? o.id}`;
            return <div key={o.id} id={anchor} className="scroll-mt-6"><WixOrderCard order={o} expanded={expandedOrders.has(anchor)} onExpandedChange={(expanded) => setExpandedOrders((previous) => {
              const next = new Set(previous);
              if (expanded) next.add(anchor); else next.delete(anchor);
              return next;
            })} /></div>;
          })}
        </div>
      )}
    </div>
  );
}
